import React, { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  useMediaQuery,
  makeStyles,
  Typography,
  Box,
  Avatar,
  CircularProgress, // Import for a loading spinner
} from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import CallEndIcon from "@mui/icons-material/CallEnd";
import MicOffIcon from "@mui/icons-material/MicOff";
import MicIcon from "@mui/icons-material/Mic";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CallIcon from "@mui/icons-material/Call";
import { useCall } from "../../../context/CallContext";

// Utility function for formatting call duration
const formatTime = (seconds) => {
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  return hrs > 0
    ? `${hrs.toString().padStart(2, "0")}:${mins
        .toString()
        .padStart(2, "0")}:${secs.toString().padStart(2, "0")}`
    : `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
};

// MUI styles
const useStyles = makeStyles((theme) => ({
  paper: ({ fullScreen }) => ({
    backgroundColor: "#1a1a1a",
    margin: 0,
    padding: 0,
    borderRadius: fullScreen ? 0 : 24,
    height: fullScreen ? "100dvh" : "auto",
    maxHeight: "100dvh",
    overflow: "hidden",
    boxSizing: "border-box",
  }),
  content: ({ fullScreen }) => ({
    padding: fullScreen ? 0 : 24,
    backgroundColor: "#1a1a1a",
    height: fullScreen ? "100%" : "auto",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    boxSizing: "border-box",
  }),
  videoWrapper: ({ fullScreen }) => ({
    width: "100%",
    height: fullScreen ? "100dvh" : "60vh",
    position: "relative",
    backgroundColor: "#000",
    borderRadius: fullScreen ? 0 : 24,
    overflow: "hidden",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  }),
  insetVideo: ({ fullScreen }) => ({
    position: "absolute",
    bottom: fullScreen ? "clamp(16px, 5%, 48px)" : 24,
    left: 16,
    width: fullScreen ? "120px" : "160px",
    height: fullScreen ? "90px" : "120px",
    maxHeight: "25%",
    borderRadius: 10,
    overflow: "hidden",
    border: "2px solid white",
    backgroundColor: "#000",
    zIndex: 2,
    boxShadow: "0 4px 12px rgba(0,0,0,0.6)",
  }),
  voiceCallContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(4),
    backgroundColor: "transparent",
    color: "white",
    height: "100%",
  },
  avatar: {
    width: theme.spacing(20),
    height: theme.spacing(20),
    marginBottom: theme.spacing(2),
  },
  endMessageContainer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    textAlign: "center",
    height: "100%",
    minHeight: "40vh",
    color: "#fff",
  },
  endMessageIcon: {
    fontSize: "4rem",
    marginBottom: theme.spacing(2),
    color: theme.palette.success.main,
  },
  duration: {
    color: "#fff",
    textAlign: "center",
    fontSize: "1rem",
    fontFamily: "monospace",
    margin: "16px 0 0",
  },
  actions: ({ fullScreen }) => ({
    justifyContent: "center",
    backgroundColor: "#1a1a1a",
    padding: fullScreen ? "12px 8px" : "16px 24px",
  }),
  statusText: {
    color: "#fff",
    opacity: 0.7,
    marginTop: theme.spacing(1),
  },
  // New styles for the status bar
  statusBar: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    padding: "8px 16px",
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 10,
    backdropFilter: "blur(5px)",
  },
  statusTextCenter: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
}));

const CallDisplayModal = ({ open, onClose }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const classes = useStyles({ fullScreen });

  const {
    localStream,
    remoteStream,
    endCall,
    isCalling,
    isReceivingCall,
    isInCall,
    callRejected,
    callEnded,
    callType,
    outgoingCallRequest,
    incomingCall,
    acceptCall,
    rejectCall,
    isReconnecting, // IMPORT THE NEW STATE
  } = useCall();

  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const remoteAudioRef = useRef(null);

  const [isMuted, setIsMuted] = useState(false);
  const [isRemoteMuted, setIsRemoteMuted] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const intervalRef = useRef(null);
  const [dialogEntered, setDialogEntered] = useState(false);
  const statsIntervalRef = useRef(null);

  const remoteUser = outgoingCallRequest?.contact || incomingCall;

  const toggleMute = () => {
    if (localStream) {
      const audioTrack = localStream.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        setIsMuted(!audioTrack.enabled);
      }
    }
  };

  const toggleRemoteMute = () => {
    const audioElement = remoteAudioRef.current;
    const videoElement = remoteVideoRef.current;
    const mediaElement = audioElement || videoElement;

    if (mediaElement) {
      mediaElement.muted = !mediaElement.muted;
      setIsRemoteMuted(mediaElement.muted);
    }
  };

  const handleEndCall = () => {
    if (isInCall) {
      endCall();
    } else {
      endCall(true, 0);
    }
  };

  useEffect(() => {
    const videoElement = localVideoRef.current;
    if (dialogEntered && videoElement && localStream && callType !== "voice") {
      videoElement.srcObject = localStream;
      videoElement.play().catch((error) => {
        console.error("Error playing local video:", error);
      });
    }
    return () => {
      if (videoElement) videoElement.srcObject = null;
    };
  }, [dialogEntered, localStream, callType]);

  useEffect(() => {
    const videoElement = remoteVideoRef.current;
    const audioElement = remoteAudioRef.current;

    if (videoElement) {
      videoElement.srcObject = null;
    }
    if (audioElement) {
      audioElement.srcObject = null;
    }

    if (remoteStream && callType !== "voice") {
      if (videoElement) {
        console.log("[UI-STREAM] Attaching remote stream to video element.");
        videoElement.srcObject = remoteStream;
        videoElement.play().catch((error) => {
          console.error(
            "[VIDEO-PLAY] ERROR: Failed to play remote video:",
            error
          );
        });
      }
    } else if (remoteStream && callType === "voice") {
      if (audioElement) {
        console.log("[UI-STREAM] Attaching remote stream to audio element.");
        audioElement.srcObject = remoteStream;
        audioElement.play().catch((error) => {
          console.error(
            "[AUDIO-PLAY] ERROR: Failed to play remote audio:",
            error
          );
        });
      }
    }

    return () => {
      if (videoElement) {
        videoElement.srcObject = null;
      }
      if (audioElement) {
        audioElement.srcObject = null;
      }
    };
  }, [remoteStream, isInCall, callType]);

  useEffect(() => {
    if (open && isInCall) {
      setElapsedTime(0);
      intervalRef.current = setInterval(() => {
        setElapsedTime((prev) => prev + 1);
      }, 1000);
    } else {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [open, isInCall]);

  const handleDialogEnter = () => {
    setDialogEntered(true);
  };

  const isVoiceOnly = callType === "voice";

  const isModalOpen =
    isCalling || isReceivingCall || isInCall || callRejected || callEnded;

  if (!isModalOpen) {
    return null;
  }

  const showCallButtons =
    (isCalling || isReceivingCall || isInCall) && !callRejected && !callEnded;
  const showDuration = isInCall;

  return (
    <Dialog
      open={isModalOpen}
      onClose={(event, reason) => {
        if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
          if (isInCall) {
            handleEndCall();
          } else if (callEnded || callRejected) {
            onClose();
          }
        }
      }}
      onEnter={handleDialogEnter}
      disableEscapeKeyDown={isInCall}
      fullScreen={fullScreen}
      fullWidth
      maxWidth="md"
      PaperProps={{ className: classes.paper }}
    >
      <DialogContent className={classes.content}>
        {isReconnecting &&
          isInCall && ( // ADD THIS CONDITIONAL RENDER
            <Box className={classes.statusBar}>
              <Typography variant="body1" className={classes.statusTextCenter}>
                <CircularProgress size={20} color="inherit" />
                Connection lost...
              </Typography>
            </Box>
          )}
        {callRejected ? (
          <Box className={classes.endMessageContainer}>
            <CallEndIcon
              style={{
                fontSize: "4rem",
                marginBottom: theme.spacing(2),
                color: theme.palette.error.main,
              }}
            />
            <Typography variant="h4" style={{ color: "white" }}>
              Call Rejected
            </Typography>
            <Typography
              variant="body1"
              style={{ color: "white", opacity: 0.8 }}
            >
              The user you were calling is unavailable.
            </Typography>
          </Box>
        ) : callEnded ? (
          <Box className={classes.endMessageContainer}>
            <CheckCircleOutlineIcon className={classes.endMessageIcon} />
            <Typography variant="h4" style={{ color: "white" }}>
              Call ended
            </Typography>
            <Typography
              variant="body1"
              style={{ color: "white", opacity: 0.8 }}
            >
              The call was terminated.
            </Typography>
          </Box>
        ) : isVoiceOnly ? (
          <Box className={classes.voiceCallContainer}>
            <audio
              ref={remoteAudioRef}
              autoPlay
              playsInline
              style={{ display: "none" }}
            />
            <Avatar
              src={remoteUser?.avatar}
              alt={remoteUser?.name}
              className={classes.avatar}
            />
            <Typography variant="h5" style={{ color: "white" }}>
              {remoteUser?.name}
            </Typography>
            <Typography
              variant="body1"
              style={{ color: "white", opacity: 0.8 }}
            >
              Voice Call
            </Typography>
          </Box>
        ) : (
          <div className={classes.videoWrapper}>
            <video
              ref={remoteVideoRef}
              playsInline
              muted={false}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
                backgroundColor: "#000",
              }}
            />
            <div className={classes.insetVideo}>
              <video
                ref={localVideoRef}
                playsInline
                muted
                style={{ width: "100%", height: "100%", objectFit: "contain" }}
              />
            </div>
          </div>
        )}
        {showDuration && <CallDuration elapsedTime={elapsedTime} />}
      </DialogContent>
      {showCallButtons && (
        <DialogActions className={classes.actions}>
          {isReceivingCall ? (
            <>
              <Tooltip title="Reject Call" arrow>
                <IconButton onClick={() => rejectCall(incomingCall.fromUserId)}>
                  <CallEndIcon style={{ color: "red" }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Accept Call" arrow>
                <IconButton onClick={acceptCall}>
                  <CallIcon style={{ color: "green" }} />
                </IconButton>
              </Tooltip>
            </>
          ) : (
            <>
              <Tooltip title={isMuted ? "Unmute" : "Mute"} arrow>
                <IconButton onClick={toggleMute}>
                  {isMuted ? (
                    <MicOffIcon style={{ color: "white" }} />
                  ) : (
                    <MicIcon style={{ color: "white" }} />
                  )}
                </IconButton>
              </Tooltip>
              <Tooltip
                title={isRemoteMuted ? "Unmute Remote" : "Mute Remote"}
                arrow
              >
                <IconButton onClick={toggleRemoteMute}>
                  {isRemoteMuted ? (
                    <VolumeOffIcon style={{ color: "white" }} />
                  ) : (
                    <VolumeUpIcon style={{ color: "white" }} />
                  )}
                </IconButton>
              </Tooltip>
              <Tooltip title="End Call" arrow>
                <IconButton onClick={handleEndCall}>
                  <CallEndIcon style={{ color: "red" }} />
                </IconButton>
              </Tooltip>
            </>
          )}
        </DialogActions>
      )}
    </Dialog>
  );
};

export default CallDisplayModal;

const CallDuration = ({ elapsedTime }) => {
  const classes = useStyles();
  return (
    <Typography className={classes.duration}>
      Duration: {formatTime(elapsedTime)}
    </Typography>
  );
};
